import { useEffect, useMemo, useState } from "react";
import api, { formatRand, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Search } from "lucide-react";
import { ViewHeader, EmptyState, StatusPill, formatDate } from "@/components/admin/AdminShared";

export const ClientsView = ({ onBack, onChanged }) => {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data));
  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(
    () => (clients || []).filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase())),
    [clients, q]
  );

  const save = async () => {
    try {
      await api.put(`/admin/clients/${editing.id}`, { name: editing.name, email: editing.email });
      toast.success("Client updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    const extra = c.orders ? ` and their ${c.orders} order(s)` : "";
    if (!window.confirm(`Delete ${c.email}${extra}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client account deleted");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-clients-view">
      <ViewHeader
        onBack={onBack}
        title={`Clients (${clients?.length ?? "…"})`}
        subtitle="Everyone who created an account on the store, and whether they have bought anything."
        action={
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9 w-64 bg-white" />
          </div>
        }
      />
      {clients && shown.length === 0 ? (
        <EmptyState>No clients found.</EmptyState>
      ) : (
        <div className="bg-white rounded-2xl border border-purple-100 overflow-x-auto">
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Bought?</TableHead>
                <TableHead>Paid total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(c.created_at)}</TableCell>
                  <TableCell className="text-sm">{c.orders}</TableCell>
                  <TableCell>
                    {c.paid_orders > 0 ? (
                      <StatusPill tone="green" testId={`client-bought-${c.id}`}>Yes · {c.paid_orders} paid</StatusPill>
                    ) : c.orders > 0 ? (
                      <StatusPill tone="amber" testId={`client-bought-${c.id}`}>Ordered, not paid</StatusPill>
                    ) : (
                      <StatusPill testId={`client-bought-${c.id}`}>No</StatusPill>
                    )}
                  </TableCell>
                  <TableCell className="font-semibold text-sm">{formatRand(c.total_spent)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`client-edit-${c.id}`} onClick={() => setEditing({ ...c })} className="p-2 rounded-lg hover:bg-purple-50 text-purple-700">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`client-delete-${c.id}`} onClick={() => remove(c)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="client-form-name" value={editing?.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="client-form-email" type="email" value={editing?.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1 bg-white" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-purple-200 font-medium">Cancel</button>
            <button data-testid="client-form-save" onClick={save} className="px-5 py-2 rounded-full brand-gradient text-white font-semibold">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
